import React, { useState } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  ComponentDTO,
  ComponentPlaceholderDTO,
  ComponentPlaceholderDTOType,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';
import Drawer from '../common/Drawer';
import ComponentPlaceholderTable from '../tables/ComponentPlaceholderTable';
import AddButton from '../common/AddButton';
import { confirmDelete } from '../../utils/sweetAlertUtils';
import Button from '../common/Button';
import ErrorField from '../common/formElements/ErrorField';

interface ComponentPlaceholder extends ComponentPlaceholderDTO {
  arrayIndex?: number;
}

interface ComponentFormProps {
  initialValues: ComponentDTO;
  onSubmit: (data: ComponentDTO) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
  placeholders: {
    id: 'placeholders',
    title: 'Placeholders',
  },
} as const;

const defaultPlaceholder: ComponentPlaceholderDTO = {
  tag: '',
  path: '',
  description: '',
  type: ComponentPlaceholderDTOType._0, // PropertyPath
  isActive: true,
};

const ComponentForm: React.FC<ComponentFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<ComponentDTO>(initialValues);
  const [placeholder, setPlaceholder] = useState<ComponentPlaceholder>(defaultPlaceholder);
  const [isPlaceholderDrawerOpen, setIsPlaceholderDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    displayPosition: [
      { required: true, message: 'Display Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Display Position must be a positive number',
      },
    ],
  };

  const placeholderValidationRules: ValidationRules = {
    tag: [{ required: true, message: 'Tag is required' }],
    description: [{ required: true, message: 'Description is required' }],
    type: [{ required: true, message: 'Type is required' }],
  };

  const { errors, validate } = useFormValidation<ComponentDTO>(validationRules);
  const { errors: placeholderErrors, validate: placeholderValidate } =
    useFormValidation<ComponentPlaceholderDTO>(placeholderValidationRules);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const handlePlaceholderSave = () => {
    if (placeholder && placeholderValidate(placeholder)) {
      setFormData((prev) => {
        const existingPlaceholders =
          (prev.placeholders as ComponentPlaceholder[]) || [];

        let existingIndex = -1;

        if (placeholder.id) {
          existingIndex = existingPlaceholders.findIndex(
            (item) => item.id === placeholder.id,
          );
        } else if (placeholder.arrayIndex !== undefined) {
          existingIndex = existingPlaceholders.findIndex(
            (item) => item.arrayIndex === placeholder.arrayIndex,
          );
        }

        let updatedPlaceholders: ComponentPlaceholder[];

        if (existingIndex !== -1) {
          // Editing existing placeholder
          updatedPlaceholders = existingPlaceholders.map((item, index) => {
            if (index === existingIndex) {
              return placeholder;
            }
            return item;
          });
        } else {
          // Adding new placeholder - assign arrayIndex
          const newArrayIndex =
            Math.max(
              ...existingPlaceholders.map((opt) => opt.arrayIndex || 0),
              0,
            ) + 1;
          const newPlaceholder = {
            ...placeholder,
            arrayIndex: newArrayIndex,
          };
          updatedPlaceholders = [...existingPlaceholders, newPlaceholder];
        }

        return {
          ...prev,
          placeholders: updatedPlaceholders,
        };
      });

      handleDrawerClose();
    }
  };

  const handlePlaceholderEdit = (placeholderToEdit: ComponentPlaceholderDTO) => {
    // Convert string enum values to numeric for the dropdown
    const normalizedPlaceholder = {
      ...placeholderToEdit,
      type: normalizeEnumValue(placeholderToEdit.type),
    };
    setPlaceholder(normalizedPlaceholder);
    setIsPlaceholderDrawerOpen(true);
  };

  const normalizeEnumValue = (type: any): ComponentPlaceholderDTOType => {
    // Handle both string and numeric enum values from API
    if (type === 'PropertyPath' || type === 0) return ComponentPlaceholderDTOType._0;
    if (type === 'SystemValue' || type === 1) return ComponentPlaceholderDTOType._1;
    if (type === 'Generic' || type === 2) return ComponentPlaceholderDTOType._2;
    return ComponentPlaceholderDTOType._0; // default
  };

  const handlePlaceholderDelete = async (placeholderToDelete: ComponentPlaceholder) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    setFormData((prev) => {
      const updatedPlaceholders =
        (prev.placeholders as ComponentPlaceholder[])?.filter((item) => {
          if (placeholderToDelete.id && item.id) {
            return item.id !== placeholderToDelete.id;
          }
          if (placeholderToDelete.arrayIndex && item.arrayIndex) {
            return item.arrayIndex !== placeholderToDelete.arrayIndex;
          }
          return item !== placeholderToDelete;
        }) || [];

      return {
        ...prev,
        placeholders: updatedPlaceholders,
      };
    });
  };

  const handleDrawerClose = () => {
    setPlaceholder(defaultPlaceholder);
    setIsPlaceholderDrawerOpen(false);
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form
      sections={sections}
      onSave={handleSave}
      onCancel={onCancel}
      isSubmitting={isLoading}
    >
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required={true}
          error={errors.title}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          helper="Lower numbers will be displayed first."
          required={true}
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active."
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.placeholders.id}
        title={FORM_SECTIONS.placeholders.title}
        headerActions={
          <AddButton
            onClick={() => {
              setPlaceholder(defaultPlaceholder);
              setIsPlaceholderDrawerOpen(true);
            }}
            variant="secondary"
            label="Add Placeholder"
          />
        }
      >
        <ComponentPlaceholderTable
          data={formData.placeholders || []}
          handleEdit={(dto) => handlePlaceholderEdit(dto)}
          handleDelete={(dto) => handlePlaceholderDelete(dto)}
          currentPage={currentPage}
          totalPages={Math.ceil(
            (formData.placeholders?.length || 0) / pageSize,
          )}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={false}
        />
        <ErrorField error={errors.placeholders} />
      </FormSection>

      <Drawer
        isOpen={isPlaceholderDrawerOpen}
        onClose={handleDrawerClose}
        title={placeholder.id || placeholder.arrayIndex !== undefined ? 'Edit Placeholder' : 'Add Placeholder'}
      >
        <div className="space-y-4">
          <FormField
            id="tag"
            label="Tag"
            type="text"
            value={placeholder.tag || ''}
            onChange={(e) => handleChange(e, setPlaceholder)}
            placeholder="e.g., *|NAME|*"
            required={true}
            error={placeholderErrors.tag}
            helper="The placeholder tag used in email templates"
          />
          <FormField
            id="path"
            label="Path"
            type="text"
            value={placeholder.path || ''}
            onChange={(e) => handleChange(e, setPlaceholder)}
            placeholder="e.g., User.FirstName"
            helper="Property path for resolution (optional for System Values)"
          />
          <FormField
            id="description"
            label="Description"
            type="text"
            value={placeholder.description || ''}
            onChange={(e) => handleChange(e, setPlaceholder)}
            placeholder="e.g., Member's first name"
            required={true}
            error={placeholderErrors.description}
          />
          <FormField
            id="type"
            label="Type"
            type="dropdown"
            value={placeholder.type !== undefined && placeholder.type !== null ? placeholder.type.toString() : ''}
            onChange={(e) => {
              const numericValue = parseInt(e.target.value, 10);
              setPlaceholder(prev => ({ ...prev, type: numericValue as ComponentPlaceholderDTOType }));
            }}
            placeholder="Select Type"
            required={true}
            error={placeholderErrors.type}
            options={[
              { value: '0', label: 'Property Path' },
              { value: '1', label: 'System Value' },
              { value: '2', label: 'Generic' },
            ]}
            helper="Property Path: extracted from entity | System Value: DateTime.Now, etc. | Generic: custom logic"
          />
          <FormField
            id="isActive"
            label="Active?"
            type="checkbox"
            value={placeholder.isActive || false}
            onChange={(e) => handleChange(e, setPlaceholder)}
            helper="Check to make placeholder active."
          />
          <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
            <Button variant="secondary" onClick={handleDrawerClose}>
              Cancel
            </Button>
            <Button type="button" onClick={handlePlaceholderSave}>
              {placeholder.id || placeholder.arrayIndex !== undefined ? 'Save Changes' : 'Add Placeholder'}
            </Button>
          </div>
        </div>
      </Drawer>
    </Form>
  );
};

export default ComponentForm;
